import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { toast } from '@/app/components/ui/simple-toaster';
import { useCreateProject } from '@/hooks/use-projects';
import { useAllCFLocations } from '@/hooks/use-cf-locations';
import { useOrganizationSettings } from '@/hooks/use-settings';
import { useSpots } from '@/hooks/use-spots';
import { useZones } from '@/hooks/use-zones';
import { handleApiError } from '@/lib/api-client';
import { DepartmentSelect } from '@/components/shared/department-select';
import { ZonePicker } from '@/components/shared/zone-picker';
import type { Project } from '@/lib/types';

interface AddJobDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (project: Project) => void;
}

interface JobFormData {
  name: string;
  client_name: string;
  start_date: string;
  end_date: string;
  location_fk: number | undefined;
  zone: number | undefined;
  spot: number | undefined;
  department: number | undefined;
}

const initialForm: JobFormData = {
  name: '',
  client_name: '',
  start_date: '',
  end_date: '',
  location_fk: undefined,
  zone: undefined,
  spot: undefined,
  department: undefined,
};

export function AddJobDialog({ open, onOpenChange, onCreated }: AddJobDialogProps) {
  const [form, setForm] = useState<JobFormData>(initialForm);
  const [error, setError] = useState<string | null>(null);
  const createMutation = useCreateProject();
  const { data: orgSettings } = useOrganizationSettings();
  const projectsEnabled = orgSettings?.modules?.projects ?? false;
  const { data: cfLocations } = useAllCFLocations();
  const { data: zonesData } = useZones(
    { location: form.location_fk, page_size: 1000 },
    !!form.location_fk
  );
  const { data: spotsData } = useSpots(
    { location: form.location_fk, zone: form.zone, page_size: 200 },
    !!form.location_fk
  );

  const updateField = (field: keyof JobFormData, value: string | number | undefined) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'location_fk') {
        next.zone = undefined;
        next.spot = undefined;
      } else if (field === 'zone') {
        next.spot = undefined;
      }
      return next;
    });
  };

  const handleZoneChange = (zoneId: number | null) => {
    updateField('zone', zoneId ?? undefined);
  };

  const handleClose = (val: boolean) => {
    if (!val) {
      setForm(initialForm);
      setError(null);
    }
    onOpenChange(val);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setError(null);

    try {
      const project = await createMutation.mutateAsync({
        name: form.name.trim(),
        client_name: form.client_name.trim() || undefined,
        start_date: form.start_date || undefined,
        end_date: form.end_date || undefined,
        location_fk: form.location_fk ?? undefined,
        zone: form.zone ?? null,
        spot: form.spot ?? null,
        department: projectsEnabled ? form.department : undefined,
      });
      toast.success(`${project.name} created`);
      handleClose(false);
      onCreated?.(project);
    } catch (err) {
      const apiError = handleApiError(err);
      setError(apiError.message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Project</DialogTitle>
          <DialogDescription>Create a new project for your organization.</DialogDescription>
        </DialogHeader>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="job-name">Project Name *</Label>
            <Input
              id="job-name"
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="job-client">Client Name</Label>
            <Input
              id="job-client"
              value={form.client_name}
              onChange={(e) => updateField('client_name', e.target.value)}
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="job-start-date">Start Date</Label>
              <Input
                id="job-start-date"
                type="date"
                value={form.start_date}
                onChange={(e) => updateField('start_date', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="job-end-date">End Date</Label>
              <Input
                id="job-end-date"
                type="date"
                min={form.start_date || undefined}
                value={form.end_date}
                onChange={(e) => updateField('end_date', e.target.value)}
              />
            </div>
          </div>
          {projectsEnabled && (
            <div className="space-y-2">
              <Label>Department *</Label>
              <DepartmentSelect
                value={form.department}
                onChange={(department) => updateField('department', department)}
                required
              />
            </div>
          )}
          <div className="space-y-2">
            <Label>Location</Label>
            <Select
              value={form.location_fk?.toString() ?? 'none'}
              onValueChange={(val) =>
                updateField('location_fk', val === 'none' ? undefined : parseInt(val))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a location (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Location</SelectItem>
                {cfLocations?.map((loc) => (
                  <SelectItem key={loc.id} value={loc.id.toString()}>
                    {loc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {form.location_fk && (zonesData?.results?.length ?? 0) > 0 && (
            <div className="space-y-2">
              <Label>Zone</Label>
              <ZonePicker
                locationId={form.location_fk}
                value={form.zone ?? null}
                onChange={handleZoneChange}
                placeholder="Select a zone (optional)"
              />
            </div>
          )}
          {form.location_fk && (spotsData?.results?.length ?? 0) > 0 && (
            <div className="space-y-2">
              <Label>Spot</Label>
              <Select
                value={form.spot?.toString() ?? 'none'}
                onValueChange={(val) =>
                  updateField('spot', val === 'none' ? undefined : parseInt(val))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a spot (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Spot</SelectItem>
                  {spotsData?.results.map((s) => (
                    <SelectItem key={s.id} value={s.id.toString()}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => handleClose(false)}>
            Cancel
          </Button>
          <Button type="submit" disabled={createMutation.isPending}>
            {createMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Add Project
          </Button>
        </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
